// Tela de gerenciamento do histórico de conversas
// Mostra 8 conversas: 4 personagens × 2 modos (Bate-papo e Praticar Idiomas)
import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import ContainerAuthenticated from "containers/Authenticated";
import { CoreContext } from "context/CoreContext";
import {
    getHistoryStats,
    deleteConversation,
    deleteAllHistory,
    RETENTION_DAYS_FREE
} from "services/conversationHistory";
import useSubscriptionCheck from "hooks/useSubscriptionCheck";

import {
    PageWrapper,
    BackButton,
    PageHeader,
    PageTitle,
    PageSubtitle,
    RetentionBanner,
    CharactersGrid,
    CharacterCard,
    CardLeft,
    CharacterImage,
    CardInfo,
    CharacterName,
    ModePill,
    CharacterStats,
    CardActions,
    ActionButton,
    EmptyState,
    EmptyIcon,
    EmptyTitle,
    EmptyText,
    FooterActions,
    DangerButton,
    ConfirmOverlay,
    ConfirmDialog,
    ConfirmTitle,
    ConfirmText,
    ConfirmButtons,
    ConfirmButton
} from "./styled";

// Catálogo de personagens
const CHARACTERS_META = {
    salomao: { name: 'Salomão', image: '/images/salomao_pixel.png' },
    daniel:  { name: 'Daniel',  image: '/images/daniel_pixel.png'  },
    ester:   { name: 'Ester',   image: '/images/ester_pixel.png'   },
    davi:    { name: 'Davi',    image: '/images/davi_pixel.png'    }
};

// Labels dos modos + caminho do ChatBox correspondente
const MODES_META = {
    chat: {
        label: 'Bate-papo',
        emoji: '💬',
        chatPath: {
            salomao: '/dashboard/chatbox05',
            daniel:  '/dashboard/chatbox06',
            ester:   '/dashboard/chatbox07',
            davi:    '/dashboard/chatbox08'
        }
    },
    learn: {
        label: 'Praticar Idiomas',
        emoji: '📚',
        chatPath: {
            salomao: '/dashboard/chatbox',
            daniel:  '/dashboard/chatbox02',
            ester:   '/dashboard/chatbox03',
            davi:    '/dashboard/chatbox04'
        }
    }
};

const formatLastDate = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const now = new Date();
        const diffMs = now - d;
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMinutes < 60) return diffMinutes <= 1 ? 'agora mesmo' : `há ${diffMinutes} minutos`;
        if (diffHours < 24)   return diffHours === 1 ? 'há 1 hora' : `há ${diffHours} horas`;
        if (diffDays < 7)     return diffDays === 1 ? 'ontem' : `há ${diffDays} dias`;
        return d.toLocaleDateString('pt-BR');
    } catch {
        return null;
    }
};

export default function DashboardHistory() {
    const history = useHistory();
    const { user } = useContext(CoreContext);
    const { hasActiveSubscription } = useSubscriptionCheck();
    const [stats, setStats] = useState([]);
    const [confirm, setConfirm] = useState(null);

    const loadStats = () => {
        if (user?.id) {
            setStats(getHistoryStats(user.id));
        }
    };

    useEffect(() => {
        loadStats();
    }, [user?.id]);

    const isPremium = hasActiveSubscription === true;

    // Conversas com pelo menos 1 mensagem
    const totalWithMessages = stats.filter(s => s.count > 0).length;

    // Ações com confirmação
    const askDeleteOne = (s) => setConfirm({
        type: 'one',
        characterId: s.characterId,
        mode: s.mode,
        characterName: CHARACTERS_META[s.characterId]?.name,
        modeLabel: MODES_META[s.mode]?.label
    });
    const askDeleteAll = () => setConfirm({ type: 'all' });
    const cancelConfirm = () => setConfirm(null);

    const doConfirm = () => {
        if (!user?.id || !confirm) return;
        if (confirm.type === 'one') {
            deleteConversation(user.id, confirm.characterId, confirm.mode);
        } else if (confirm.type === 'all') {
            deleteAllHistory(user.id);
        }
        loadStats();
        setConfirm(null);
    };

    const goToConversation = (characterId, mode) => {
        const path = MODES_META[mode]?.chatPath?.[characterId];
        if (path) history.push(path);
    };

    const goBack = () => history.push('/dashboard');

    return (
        <ContainerAuthenticated primary page={'history'}>
            <PageWrapper>
                <BackButton onClick={goBack} aria-label="Voltar para o dashboard">← Voltar</BackButton>

                <PageHeader>
                    <PageTitle>📚 Minhas Conversas</PageTitle>
                    <PageSubtitle>
                        Suas conversas com cada personagem ficam guardadas neste navegador, separadas por modo.
                    </PageSubtitle>
                </PageHeader>

                {/* Aviso permanente: histórico fica só no aparelho */}
                <RetentionBanner subtle>
                    🔒 Suas conversas ficam salvas <strong>apenas neste aparelho</strong> e não são transferidas para outros dispositivos.
                </RetentionBanner>

                {!isPremium && (
                    <RetentionBanner>
                        💡 Suas conversas ficam guardadas por <strong>{RETENTION_DAYS_FREE} dias</strong>. Com o Premium, elas ficam para sempre.
                    </RetentionBanner>
                )}

                {totalWithMessages === 0 ? (
                    <EmptyState>
                        <EmptyIcon>💬</EmptyIcon>
                        <EmptyTitle>Nenhuma conversa ainda</EmptyTitle>
                        <EmptyText>
                            Comece a conversar com algum dos personagens — em Bate-papo ou Praticar Idiomas — e suas mensagens aparecerão aqui.
                        </EmptyText>
                    </EmptyState>
                ) : (
                    <>
                        <CharactersGrid>
                            {stats.map((s) => {
                                const char = CHARACTERS_META[s.characterId];
                                const modeInfo = MODES_META[s.mode];
                                const hasMessages = s.count > 0;
                                const key = `${s.characterId}_${s.mode}`;

                                return (
                                    <CharacterCard key={key} dim={!hasMessages}>
                                        <CardLeft>
                                            <CharacterImage src={char?.image} alt={char?.name} width="64" height="64" />
                                            <CardInfo>
                                                <CharacterName>
                                                    {char?.name}
                                                    <ModePill mode={s.mode}>
                                                        {modeInfo?.emoji} {modeInfo?.label}
                                                    </ModePill>
                                                </CharacterName>
                                                <CharacterStats>
                                                    {hasMessages
                                                        ? <>{s.count} mensage{s.count > 1 ? 'ns' : 'm'} · Última {formatLastDate(s.lastTimestamp)}</>
                                                        : <>Sem conversas ainda</>}
                                                </CharacterStats>
                                            </CardInfo>
                                        </CardLeft>
                                        <CardActions>
                                            {hasMessages && (
                                                <>
                                                    <ActionButton onClick={() => goToConversation(s.characterId, s.mode)} title={`Continuar conversa`}>
                                                        Continuar
                                                    </ActionButton>
                                                    <ActionButton danger onClick={() => askDeleteOne(s)} title={`Apagar conversa`}>
                                                        🗑️ Apagar
                                                    </ActionButton>
                                                </>
                                            )}
                                            {!hasMessages && (
                                                <ActionButton onClick={() => goToConversation(s.characterId, s.mode)}>
                                                    Começar conversa
                                                </ActionButton>
                                            )}
                                        </CardActions>
                                    </CharacterCard>
                                );
                            })}
                        </CharactersGrid>

                        <FooterActions>
                            <DangerButton onClick={askDeleteAll}>
                                🗑️ Apagar TODAS as conversas
                            </DangerButton>
                        </FooterActions>
                    </>
                )}

                {confirm && (
                    <ConfirmOverlay onClick={cancelConfirm}>
                        <ConfirmDialog onClick={(e) => e.stopPropagation()}>
                            <ConfirmTitle>
                                {confirm.type === 'all'
                                    ? '⚠️ Apagar todas as conversas?'
                                    : `🗑️ Apagar conversa de ${confirm.characterName} no modo ${confirm.modeLabel}?`}
                            </ConfirmTitle>
                            <ConfirmText>
                                {confirm.type === 'all'
                                    ? 'Isso vai apagar TODO o seu histórico de conversas com todos os personagens (Bate-papo e Praticar Idiomas). Esta ação não pode ser desfeita.'
                                    : `Suas mensagens com ${confirm.characterName} no modo ${confirm.modeLabel} serão apagadas permanentemente. Esta ação não pode ser desfeita.`}
                            </ConfirmText>
                            <ConfirmButtons>
                                <ConfirmButton onClick={cancelConfirm}>Cancelar</ConfirmButton>
                                <ConfirmButton danger onClick={doConfirm}>
                                    {confirm.type === 'all' ? 'Sim, apagar TUDO' : 'Sim, apagar'}
                                </ConfirmButton>
                            </ConfirmButtons>
                        </ConfirmDialog>
                    </ConfirmOverlay>
                )}
            </PageWrapper>
        </ContainerAuthenticated>
    );
}
